import React, { useState, useEffect } from 'react';
import { ShieldCheck, Heart, Plus, Sparkles, Menu, X, Home, User, LogOut, LayoutDashboard, Globe } from 'lucide-react';
import Logo from './Logo';
import { useLanguage } from '../lib/LanguageContext';
import { useRouter } from '../lib/router';

interface NavbarProps {
  profileImage?: string | null;
  onAddPropertyClick: () => void;
  onDashboardClick: () => void;
  favoritesCount: number;
  onShowFavoritesOnly: (show: boolean) => void;
  isShowingFavorites: boolean;
  userSession: any;
  onAuthClick: () => void;
  onLogout: () => void;
  onProfileEditClick: () => void;
  currentPage: 'home' | 'browse' | 'favorites';
  onNavigate: (page: 'home' | 'browse' | 'favorites') => void;
}

export default function Navbar({
  profileImage,
  onAddPropertyClick,
  onDashboardClick,
  favoritesCount,
  onShowFavoritesOnly,
  isShowingFavorites,
  userSession,
  onAuthClick,
  onLogout,
  onProfileEditClick,
  currentPage,
  onNavigate
}: NavbarProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { language, setLanguage, t, isRtl } = useLanguage();
  const { navigate } = useRouter();

  const isVerified = userSession?.user?.user_metadata?.verified_status === 'approved' || !!userSession?.user?.user_metadata?.verified;

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 40);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    setMobileMenuOpen(false);
    navigate('/');
    setTimeout(() => {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const handleLogoClick = () => {
    navigate('/');
  };

  return (
    <nav
      id="main-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled
          ? 'py-3 bg-white/80 backdrop-blur-xl shadow-lg border-b border-gray-100/50'
          : 'py-6 bg-transparent'
      }`}
      dir={isRtl ? 'rtl' : 'ltr'}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 sm:h-20 gap-2 sm:gap-4 relative w-full">
          
          {/* Left side items: Desktop Nav & Mobile Menu Trigger */}
          <div className="flex-1 flex items-center justify-start gap-2 min-w-0">
            {/* Desktop Navigation Links */}
            <div className="hidden lg:flex items-center gap-7 font-sans text-sm font-medium text-gray-600">
              <button
                onClick={() => navigate('/')}
                className={`pb-1 transition-all duration-200 cursor-pointer ${
                  currentPage === 'home'
                    ? 'text-brand-emerald font-extrabold border-b-2 border-brand-emerald px-1'
                    : 'hover:text-brand-emerald'
                }`}
              >
                {t('nav.home')}
              </button>
              <button
                onClick={() => navigate('/browse')}
                className={`pb-1 transition-all duration-200 cursor-pointer ${
                  currentPage === 'browse'
                    ? 'text-brand-emerald font-extrabold border-b-2 border-brand-emerald px-1'
                    : 'hover:text-brand-emerald'
                }`}
              >
                {t('nav.browse')}
              </button>
            </div>
            
            {/* Mobile Menu Trigger */}
            <div className="lg:hidden flex items-center relative z-20">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2.5 bg-gray-50 hover:bg-gray-100 text-gray-700 rounded-xl transition-all"
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>

            {/* Favorites Toggle - Moved to Left Side */}
            <div className="hidden lg:flex items-center">
              <button
                id="fav-toggle-btn"
                onClick={() => navigate('/favorites')}
                className={`relative flex items-center justify-center gap-2 p-2.5 xl:px-4 xl:py-2.5 rounded-2xl text-xs font-bold transition-all duration-300 cursor-pointer shrink-0 ${
                  currentPage === 'favorites'
                    ? 'bg-red-50 text-red-600 border border-red-200 shadow-sm'
                    : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border border-transparent'
                }`}
              >
                <Heart className={`w-4 h-4 ${currentPage === 'favorites' ? 'fill-red-500 text-red-500' : ''}`} />
                <span className="hidden xl:inline">{t('nav.favorites')}</span>
                {favoritesCount > 0 && (
                  <span className="absolute -top-1.5 -left-1.5 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold animate-bounce">
                    {favoritesCount}
                  </span>
                )}
              </button>
            </div>
          </div>

          {/* Logo Section - Centered cleanly in flex flow */}
          <div className="flex-shrink flex items-center justify-center z-10 px-2 lg:px-4 min-w-0">
            <button className="flex items-center justify-center gap-3 cursor-pointer bg-transparent border-none p-0 outline-none hover:opacity-80 transition-opacity w-full max-w-full" onClick={handleLogoClick}>
              <Logo variant="landscape" />
            </button>
          </div>

          {/* Right side items: Actions */}
          <div className="flex-1 flex items-center justify-end gap-2 min-w-0 relative z-20">
            {/* Desktop Actions */}
            <div className="hidden lg:flex items-center gap-2 lg:gap-2.5 xl:gap-5 font-sans">
              {/* Auth Button / Profile Indicator */}
              {userSession ? (
                <div className="flex items-center gap-1.5 lg:gap-2 xl:gap-3">
                  <button
                    onClick={() => navigate('/dashboard')}
                    className="flex items-center justify-center gap-1.5 p-2.5 xl:px-3.5 xl:py-2.5 rounded-2xl text-xs font-bold transition-all cursor-pointer shrink-0 bg-blue-50 hover:bg-blue-100 text-blue-600 border border-blue-100"
                  >
                    <LayoutDashboard className="w-3.5 h-3.5" />
                    <span className="hidden xl:inline">{t('nav.dashboard')}</span>
                  </button>
                  
                  <div className="flex items-center gap-2 bg-gray-50/80 border border-gray-100 p-1.5 xl:px-3 xl:py-1.5 rounded-2xl shrink-0">
                    {profileImage ? (
                      <img 
                        src={profileImage} 
                        alt="Profile" 
                        referrerPolicy="no-referrer"
                        className="w-7 h-7 rounded-full object-cover border border-brand-emerald/20 shadow-sm"
                        loading="lazy"
                        decoding="async"
                      />
                    ) : (
                      <div className="w-7 h-7 rounded-full bg-brand-emerald/10 text-brand-emerald flex items-center justify-center font-black text-xs border border-brand-emerald/20 shadow-sm">
                        {userSession.user?.user_metadata?.full_name?.charAt(0) || 'م'}
                      </div>
                    )}
                    <div className="hidden xl:block text-right">
                      <span className="text-xs font-black text-gray-800 block max-w-[80px] truncate leading-tight">
                        {userSession.user?.user_metadata?.full_name?.split(' ')[0] || t('nav.owner')}
                      </span>
                      {isVerified ? (
                        <span className="text-[8px] text-brand-emerald font-black block leading-none mt-0.5">{t('nav.certified_member')}</span>
                      ) : (
                        <span className="text-[8px] text-gray-400 font-black block leading-none mt-0.5">{t('nav.registered_member')}</span>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => navigate('/login')}
                  className="flex items-center gap-1.5 bg-gray-50 hover:bg-gray-150 border border-gray-200/80 text-gray-700 px-3 lg:px-4 py-2.5 rounded-2xl text-xs font-bold transition-all cursor-pointer shrink-0"
                >
                  <User className="w-3.5 h-3.5 text-brand-emerald" />
                  <span className="hidden lg:inline">{t('nav.login')}</span>
                  <span className="lg:hidden">{t('nav.login_short')}</span>
                </button>
              )}

              {/* Language Switcher for Desktop */}
              <button
                onClick={() => setLanguage(language === 'ar' ? 'en' : 'ar')}
                className="flex items-center justify-center p-2.5 bg-gray-50 hover:bg-gray-100 border border-gray-200 text-gray-700 rounded-2xl text-xs font-bold transition-all cursor-pointer shrink-0"
                title={language === 'ar' ? 'Switch to English' : 'التحويل للعربية'}
              >
                <Globe className="w-4 h-4 text-brand-emerald" />
              </button>

              {/* Elegant vertical divider to separate actions from primary CTA */}
              <div className="h-6 w-[1px] bg-gray-300 mx-0.5 lg:mx-1 shrink-0" />

              {/* Sign Out CTA */}
              {userSession && (
                <button
                  id="sign-out-btn"
                  onClick={onLogout}
                  className="relative flex items-center justify-center gap-2 bg-red-50 text-red-600 px-3.5 py-2.5 xl:px-5 xl:py-2.5 rounded-2xl text-xs font-bold hover:bg-red-500 hover:text-white hover:shadow-lg hover:shadow-red-500/20 transition-all duration-300 cursor-pointer overflow-hidden group shrink-0"
                >
                  <LogOut className="w-4 h-4" />
                  <span className="hidden xl:inline">{t('nav.logout')}</span>
                  <div className="absolute inset-0 bg-gradient-to-tr from-white/0 via-white/10 to-white/0 transition-transform duration-1000 translate-x-[-100%] group-hover:translate-x-[100%]" />
                </button>
              )}
            </div>

            {/* Mobile Actions */}
            <div className="lg:hidden flex items-center gap-2">
              {/* Mobile Language Switcher */}
              <button
                onClick={() => setLanguage(language === 'ar' ? 'en' : 'ar')}
                className="p-2.5 bg-gray-50 text-gray-600 hover:bg-gray-100 rounded-xl transition-all"
                title={language === 'ar' ? 'Switch to English' : 'التحويل للعربية'}
              >
                <Globe className="w-4 h-4 text-brand-emerald" />
              </button>

              {/* Mobile Favorites Button */}
              <button
                onClick={() => {
                  setMobileMenuOpen(false);
                  navigate('/favorites');
                }}
                className={`p-2.5 rounded-xl transition-all relative ${
                  currentPage === 'favorites' ? 'bg-red-50 text-red-600' : 'bg-gray-50 text-gray-600'
                }`}
              >
                <Heart className={`w-4 h-4 ${currentPage === 'favorites' ? 'fill-red-500 text-red-500' : ''}`} />
                {favoritesCount > 0 && (
                  <span className="absolute -top-1 -left-1 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold">
                    {favoritesCount}
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Drawer */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-white/95 backdrop-blur-2xl border-b border-gray-100 py-6 px-4 absolute top-full left-0 right-0 shadow-xl flex flex-col gap-5 transition-all duration-300 animate-in fade-in slide-in-from-top-4">
          <div className={`flex flex-col gap-4 ${isRtl ? 'text-right' : 'text-left'}`}>
            <button
              onClick={() => {
                setMobileMenuOpen(false);
                navigate('/');
              }}
              className={`py-2.5 text-sm font-semibold border-b border-gray-50 ${isRtl ? 'text-right' : 'text-left'} ${currentPage === 'home' ? 'text-brand-emerald font-extrabold' : 'text-gray-800'}`}
            >
              {t('nav.home')}
            </button>
            <button
              onClick={() => {
                setMobileMenuOpen(false);
                navigate('/browse');
              }}
              className={`py-2.5 text-sm font-semibold border-b border-gray-50 ${isRtl ? 'text-right' : 'text-left'} ${currentPage === 'browse' ? 'text-brand-emerald font-extrabold' : 'text-gray-800'}`}
            >
              {t('nav.browse')}
            </button>
          </div>

          <div className="flex flex-col gap-3">
            {userSession ? (
              <div className="flex flex-col gap-2">
                <button
                  onClick={() => {
                    setMobileMenuOpen(false);
                    navigate('/dashboard/profile');
                  }}
                  className="flex items-center justify-center gap-2 bg-brand-emerald/10 text-brand-emerald py-3 rounded-2xl text-xs font-bold border border-brand-emerald/20 cursor-pointer"
                >
                  <User className="w-4 h-4 text-brand-emerald" />
                  <span>{t('nav.edit_profile')}</span>
                </button>

                <button
                  onClick={() => {
                    setMobileMenuOpen(false);
                    navigate('/dashboard');
                  }}
                  className="flex items-center justify-center gap-2 bg-blue-50 text-blue-600 py-3 rounded-2xl text-xs font-bold border border-blue-100 cursor-pointer"
                >
                  <LayoutDashboard className="w-4 h-4" />
                  <span>{t('nav.dashboard')}</span>
                </button>
                
                <div className={`bg-gray-50 border border-gray-100 p-4 rounded-2xl flex items-center justify-between ${isRtl ? 'flex-row' : 'flex-row-reverse'}`}>
                  <div className={`flex items-center gap-2.5 ${isRtl ? 'flex-row' : 'flex-row-reverse'}`}>
                    {profileImage ? (
                      <img 
                        src={profileImage} 
                        alt="Profile" 
                        referrerPolicy="no-referrer"
                        className="w-10 h-10 rounded-full object-cover border border-brand-emerald/20"
                        loading="lazy"
                        decoding="async"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-brand-emerald/10 text-brand-emerald flex items-center justify-center font-black text-sm border border-brand-emerald/20">
                        {userSession.user?.user_metadata?.full_name?.charAt(0) || 'м'}
                      </div>
                    )}
                    <div className={isRtl ? 'text-right' : 'text-left'}>
                      <span className="text-xs font-black text-gray-800 block">
                        {userSession.user?.user_metadata?.full_name || t('nav.owner')}
                      </span>
                      {isVerified ? (
                        <span className="text-xs text-brand-emerald font-semibold block mt-0.5">{t('nav.certified_member')}</span>
                      ) : (
                        <span className="text-xs text-gray-400 font-semibold block mt-0.5">{t('nav.registered_member')}</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <button
                onClick={() => {
                  setMobileMenuOpen(false);
                  navigate('/login');
                }}
                className="flex items-center justify-center gap-2 bg-gray-50 hover:bg-gray-100 border border-gray-200 text-gray-700 py-3 rounded-2xl text-xs font-bold transition-all cursor-pointer"
              >
                <User className="w-4 h-4 text-brand-emerald" />
                <span>{t('nav.login')}</span>
              </button>
            )}

            {userSession && (
              <button
                onClick={() => {
                  setMobileMenuOpen(false);
                  onLogout();
                }}
                className="flex items-center justify-center gap-2 bg-red-50 text-red-600 py-3.5 rounded-2xl text-xs font-bold shadow-sm shadow-red-500/10 cursor-pointer hover:bg-red-500 hover:text-white transition-all"
              >
                <LogOut className="w-4 h-4" />
                <span>{t('nav.logout')}</span>
              </button>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
